import React, { useLayoutEffect, useRef, useState } from 'react';
import { SunLoader } from './SunLoader.js';

/**
 * Знак ожидания в одном из двух стандартных положений (правило владельца
 * 05.10.2026). Размеры — в одном месте: `--loader-area` и `--loader-row` в
 * `:root` (styles.css).
 *
 * `area` — грузится целая область или страница: крупный знак по центру
 * свободного места, под ним тихая подпись. Образец — поиск раздач. Область
 * тянется от своего верха до низа окна (`useFillToViewport`), поэтому знак
 * стоит по центру того, что видит зритель, а прокрутки не появляется.
 *
 * `row` — подгружается строка (ряд карточек, список серий, лента): знак
 * строчного размера ровно посередине этой строки, по горизонтали и вертикали.
 * Высоту строки резервирует место показа (`--loader-row-h`), чтобы пришедшее
 * содержимое ничего не сдвинуло. Подпись не видна, но остаётся текстом: её
 * читает экранный диктор.
 */
export function Loader({
  variant,
  label,
  className,
}: {
  variant: 'area' | 'row';
  /** Что ждём: подпись под знаком области и текст для чтения с экрана. */
  label: string;
  className?: string;
}): React.JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const minHeight = useFillToViewport(host, variant === 'area');
  const classes = ['loader', variant === 'area' ? 'loader-area' : 'loader-row', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <div ref={host} className={classes} style={minHeight !== null ? { minHeight } : undefined} role="status">
      <SunLoader className="loader-sign" />
      <span className={variant === 'area' ? 'loader-caption' : 'loader-label'}>{label}</span>
    </div>
  );
}

/** Ниже этого область не сжимается: знаку с подписью нужно место. */
const AREA_MIN_HEIGHT = 200;

/**
 * Высота области ожидания — от верха блока до низа окна.
 *
 * Сначала — до низа окна от верха блока, затем вычитаем то, что под блоком
 * ещё есть у страницы (поля, нижнее меню телефона): иначе появилась бы
 * прокрутка на эти несколько десятков пикселей. `null` — не мерили.
 */
export function useFillToViewport(host: React.RefObject<HTMLElement | null>, active: boolean): number | null {
  const [minHeight, setMinHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!active) return undefined;
    const measure = (): void => {
      const element = host.current;
      if (element === null) return;
      const top = element.getBoundingClientRect().top + window.scrollY;
      const first = Math.max(AREA_MIN_HEIGHT, window.innerHeight - top);
      element.style.minHeight = `${first}px`;
      const over = document.documentElement.scrollHeight - window.innerHeight;
      setMinHeight(Math.max(AREA_MIN_HEIGHT, first - Math.max(0, over)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [active, host]);
  // Последняя мерка остаётся и после выключения: знак, который растворяется
  // поверх пришедших результатов, не должен при этом подпрыгнуть.
  return minHeight;
}
